<template>
	<v-dialog v-model="visible" max-width="360">
		<template v-slot:activator="{ on, attrs }">
			<slot v-bind="{ on, attrs }" />
		</template>

		<v-card :loading="loading">
			<layout-error-boundary
				ref="errBoundary"
				v-slot="{ hasError, err }"
				:on-error="handleFormError"
			>
				<v-form ref="form" lazy-validation @submit.prevent="submit">
					<v-card-title class="headline">{{
						strings.dialogs.addRoom.title
					}}</v-card-title>
					<v-card-text>
						<v-alert
							v-if="hasError"
							type="error"
							border="left"
							dismissible
							outlined
							@click.native="$refs.errBoundary.resetError()"
						>
							<span class="error--text">{{
								`${strings.dialogs.addRoom.error}${
									err.message ? ": " + err.message : ""
								}`
							}}</span>
						</v-alert>

						<v-text-field
							v-model="name"
							:rules="nameRules"
							label="Room name"
							type="text"
						/>
					</v-card-text>

					<v-card-actions>
						<v-spacer />
						<v-btn color="rust-red" text @click="visible = false">Cancel</v-btn>
						<v-btn color="ocean darken-1" type="submit" outlined
							>Add Room</v-btn
						>
					</v-card-actions>
				</v-form>
			</layout-error-boundary>
		</v-card>
	</v-dialog>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";

import { DEVICE_CLASS_ROOM, NodeOperationError } from "@/utils/k4util";
import { APIError, APIAuthError } from "@/utils/http";
import strings from "@/utils/strings";

const initData = () => ({
	strings,
	visible: false,
	loading: false,

	resolve: null,
	handleSubmit: null,

	name: null,
	nameRules: [v => !!_.trim(v).length || "Name is required"],
});

export default {
	data: initData,

	computed: {
		...mapGetters({
			rootNode: "devices/rootNode",
		}),

		roomDescription() {
			const descriptions = this.$store.state.descriptions.items;
			return _.chain(descriptions)
				.find({ cls: DEVICE_CLASS_ROOM })
				.get("data")
				.value();
		},
	},

	watch: {
		visible(is, was) {
			// Reset all relevant data whenever dialog closes
			if (was && !is) this.reset();
		},
	},

	async beforeMount() {
		this.loading = true;
		try {
			// Load descriptions
			await this.$store.dispatch("descriptions/load");
		} finally {
			this.loading = false;
		}
	},

	methods: {
		...mapActions({
			add: "controller/nodes/addRoom",
		}),

		reset() {
			Object.assign(this, initData());
			this.$refs.errBoundary.resetError();
			this.$refs.form.resetValidation();
		},

		pop(handleSubmit) {
			this.visible = true;
			this.handleSubmit = handleSubmit;
			return new Promise(resolve => {
				this.resolve = resolve;
			});
		},

		async submit() {
			const valid = this.$refs.form.validate();
			if (!valid) return;

			this.loading = true;

			const formPayload = {
				name: this.name,
				parent: null,
				description: this.roomDescription,
				subpath: "Devices",
			};
			try {
				let resp = formPayload;
				if (this.handleSubmit) {
					resp = await this.handleSubmit(formPayload);
				}
				if (this.resolve) {
					this.resolve(resp);
				}
				this.visible = false;
			} finally {
				this.loading = false;
			}
		},

		handleFormError(err) {
			// We only want to handle certain types of errors. Keep propagating
			// any others.
			const isApiErr =
				err instanceof APIError && !(err instanceof APIAuthError);
			const isNodeOpErr = err instanceof NodeOperationError;
			return !(isApiErr || isNodeOpErr);
		},
	},
};
</script>
